import { ErrorBoundary } from 'react-error-boundary';

import { percentFormat } from '@/utils/hooks/format/useFormatNumber';
// Hooks
import useAllSocialProfilesAnalytics from '@/utils/hooks/swr/useAllSocialProfilesAnalytics';
import useSocialProfiles from '@/utils/hooks/swr/useSocialProfiles';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Components
import KeyNumberKpi from '@/components/pages/home/keyNumbers/KeyNumberKpi';
import KeyNumberMost from '@/components/pages/home/keyNumbers/KeyNumberMost';

const ErrorKeyNumbersFallback = ({ resetErrorBoundary }: { resetErrorBoundary: () => void }) => {
    const { t } = useTranslation();

    return (
        <div
            role="alert"
            className="mt-8 flex h-80 w-full flex-1 flex-col items-center justify-center space-y-3 rounded bg-white p-8 dark:bg-icoDarkMode-anthracite">
            <p className="text-center">{t('errors:error_occured_fetch_data')}</p>
            <button className="text-icoBlue underline" onClick={resetErrorBoundary}>
                {t('commons:try_again')}
            </button>
        </div>
    );
};

const KeyNumbers = () => {
    const { t } = useTranslation('home');

    const { activatedSocialProfiles } = useSocialProfiles({
        revalidateOnFocus: false,
        revalidateOnMount: false,
        revalidateOnReconnect: false
    });
    const { allSocialProfilesAnalytics, mutateAllSocialProfilesAnalytics, isLoading } =
        useAllSocialProfilesAnalytics({
            revalidateOnFocus: false,
            revalidateIfStale: false
        });

    const spEngagedPost = activatedSocialProfiles?.find(
        (sp) => sp.id === allSocialProfilesAnalytics?.most_engaged_post?.social_profile_id
    );
    const spReachedPost = activatedSocialProfiles?.find(
        (sp) => sp.id === allSocialProfilesAnalytics?.most_reached_post?.social_profile_id
    );

    return (
        <ErrorBoundary
            FallbackComponent={ErrorKeyNumbersFallback}
            onReset={() => mutateAllSocialProfilesAnalytics()}>
            <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-4">
                <KeyNumberKpi
                    isLoading={isLoading}
                    title={t('key_numbers.kpi_1_title')}
                    icon="posts"
                    iconColor="text-icoBlue dark:text-icoDarkMode-darkBlue"
                    bgColor="bg-icoBlue-100 dark:bg-icoBlue/20"
                    value={allSocialProfilesAnalytics?.analytics?.posts}
                    evolution={false}
                    spDisplay={true}
                />
                <KeyNumberKpi
                    isLoading={isLoading}
                    title={t('key_numbers.kpi_2_title')}
                    icon="followers"
                    iconColor="text-icoBlue dark:text-icoDarkMode-darkBlue"
                    bgColor="bg-icoBlue-100 dark:bg-icoBlue/20"
                    value={allSocialProfilesAnalytics?.analytics?.followers_evolution}
                />
                <KeyNumberKpi
                    isLoading={isLoading}
                    title={t('key_numbers.kpi_3_title')}
                    icon="engagement-on"
                    iconColor="text-icoGreen dark:text-icoDarkMode-green"
                    bgColor="bg-icoGreen-100 dark:bg-icoGreen/20"
                    value={allSocialProfilesAnalytics?.analytics?.engagement_rate}
                    valueFormat={percentFormat}
                />
                <KeyNumberKpi
                    isLoading={isLoading}
                    title={t('key_numbers.kpi_4_title')}
                    icon="impression"
                    iconColor="text-icoPurple dark:text-icoDarkMode-purple"
                    bgColor="bg-icoPurple-100 dark:bg-icoPurple/20"
                    value={allSocialProfilesAnalytics?.analytics?.impressions}
                />
            </div>
            <div className="mt-5 grid grid-cols-1 gap-5 @3xl:grid-cols-2">
                <KeyNumberMost
                    title={t('key_numbers.most_reached_post')}
                    sp={spReachedPost}
                    post={allSocialProfilesAnalytics?.most_reached_post}
                    value={allSocialProfilesAnalytics?.most_reached_post?.reach}
                    icon="reach"
                    bgColor="bg-icoYellow-100 dark:bg-icoYellow/20"
                    borderColor="border-icoYellow-200 dark:border-icoDarkMode-yellow"
                    textColor="text-icoYellow"
                    isLoading={isLoading}
                />
                <KeyNumberMost
                    title={t('key_numbers.most_engaged_post')}
                    sp={spEngagedPost}
                    post={allSocialProfilesAnalytics?.most_engaged_post}
                    value={allSocialProfilesAnalytics?.most_engaged_post?.engagements}
                    icon="engagement-on"
                    bgColor="bg-icoGreen-100 dark:bg-icoGreen/20"
                    borderColor="border-icoGreen-200 dark:border-icoDarkMode-green"
                    textColor="text-icoGreen dark:text-icoDarkMode-green"
                    isLoading={isLoading}
                />
            </div>
        </ErrorBoundary>
    );
};

export default KeyNumbers;
